import { TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { styled } from "@mui/material/styles";

const StyledStepper = styled(Stepper)(({ theme }) => ({
  backgroundColor: "transparent",
  padding: theme.spacing(3, 0, 2),
  "& .MuiStepLabel-label": {
    fontSize: "0.9rem",
    fontWeight: 500,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 600,
  margin: "0 auto",
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const FormSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
  },
}));

const TeamInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  width: "100%",
}));

const HorizontalStepper = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = React.useState(0);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const steps = React.useMemo(
    () => [
      t("stepper.steps.team"),
      t("stepper.steps.overs"),
      t("stepper.steps.batting"),
    ],
    [t],
  );
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const initialValues = {
    team1: "",
    team2: "",
    maxOver: "",
    batting: "",
  };
  const validationSchema = [
    Yup.object().shape({
      team1: Yup.string().required(t("validation.teamNameRequired")),
      team2: Yup.string().required(t("validation.teamNameRequired")),
    }),
    Yup.object().shape({
      maxOver: Yup.string().required(t("validation.overRequired")),
    }),
    Yup.object().shape({
      batting: Yup.string().required(t("validation.battingRequired")),
    }),
  ];
  const currentValidationSchema = validationSchema[activeStep];
  function isLastStep() {
    return activeStep === steps.length - 1;
  }
  return (
    <Box>
      <StyledStepper activeStep={activeStep} orientation="horizontal">
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </StyledStepper>

      <StyledCard elevation={3}>
        <StyledCardContent>
          <Formik
            enableReinitialize
            validationSchema={currentValidationSchema}
            initialValues={initialValues}
            onSubmit={(values, actions) => {
              handleNext();
              actions.setTouched({});
              actions.setSubmitting(false);
              if (isLastStep()) {
                setSubmitting(true);
                const data = JSON.stringify(values);
                localStorage.setItem("data", data);
                navigate("/score");
                setSubmitting(false);
              }
            }}
          >
            {(prp) => {
              const {
                values,
                touched,
                errors,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
              } = prp;
              return (
                <form onSubmit={handleSubmit}>
                  {activeStep === 0 && (
                    <FormSection>
                      <Typography variant="h6" gutterBottom>
                        {t("stepper.teamNames.title")}
                      </Typography>
                      <TeamInputContainer>
                        <StyledTextField
                          id="team1"
                          name="team1"
                          label={t("stepper.teamNames.team1Label")}
                          value={values.team1}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={
                            errors.team1 && touched.team1 && errors.team1
                          }
                          error={errors.team1 && touched.team1}
                          fullWidth
                          variant="outlined"
                        />
                        <Typography
                          variant="h4"
                          sx={{ color: "primary.main", fontWeight: 500 }}
                        >
                          {t("stepper.teamNames.vs")}
                        </Typography>
                        <StyledTextField
                          id="team2"
                          name="team2"
                          label={t("stepper.teamNames.team2Label")}
                          value={values.team2}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={
                            errors.team2 && touched.team2 && errors.team2
                          }
                          error={errors.team2 && touched.team2}
                          fullWidth
                          variant="outlined"
                        />
                      </TeamInputContainer>
                    </FormSection>
                  )}
                  {activeStep === 1 && (
                    <FormSection>
                      <Typography variant="h6" gutterBottom>
                        {t("stepper.overs.title")}
                      </Typography>
                      <StyledTextField
                        id="maxOver"
                        name="maxOver"
                        label={t("stepper.overs.label")}
                        value={values.maxOver}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText={
                          errors.maxOver && touched.maxOver && errors.maxOver
                        }
                        error={errors.maxOver && touched.maxOver}
                        fullWidth
                        variant="outlined"
                        type="number"
                        inputProps={{ min: 1, max: 50 }}
                      />
                    </FormSection>
                  )}
                  {activeStep === 2 && (
                    <FormSection>
                      <Typography variant="h6" gutterBottom>
                        {t("stepper.batting.question")}
                      </Typography>
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          name="batting"
                          value={values.batting.toString()}
                          onChange={(event) => {
                            setFieldValue("batting", event.currentTarget.value);
                          }}
                        >
                          <FormControlLabel
                            value={values.team1}
                            control={<Radio />}
                            label={
                              <Typography variant="body1">
                                {values.team1}
                              </Typography>
                            }
                            sx={{
                              padding: 2,
                              borderRadius: 1,
                              margin: 0.5,
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          />
                          <FormControlLabel
                            value={values.team2}
                            control={<Radio />}
                            label={
                              <Typography variant="body1">
                                {values.team2}
                              </Typography>
                            }
                            sx={{
                              padding: 2,
                              borderRadius: 1,
                              margin: 0.5,
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          />
                        </RadioGroup>
                      </FormControl>
                    </FormSection>
                  )}
                  <ButtonContainer>
                    <Button
                      variant="outlined"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      size="large"
                    >
                      {t("stepper.buttons.back")}
                    </Button>
                    <Button
                      id="submit"
                      disabled={isSubmitting}
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                    >
                      {isLastStep()
                        ? t("stepper.buttons.startGame")
                        : t("stepper.buttons.next")}
                    </Button>
                  </ButtonContainer>
                </form>
              );
            }}
          </Formik>
        </StyledCardContent>
      </StyledCard>
    </Box>
  );
};

export default HorizontalStepper;
